import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Facebook, Mail, MessageCircle, MessageSquare, Play, Share2 } from "lucide-react";

const ALLOWED = [/^https:\/\/(www\.)?youtube\.com\/shorts\/[\w-]{6,20}$/, /^https:\/\/youtu\.be\/[\w-]{6,20}$/];
const safeUrl = (v) => (v && (ALLOWED.some((r) => r.test(v)) || v.startsWith(`${window.location.origin}/m/`)) ? v : null);

const Option = ({ href, onClick, icon: Icon, label, testid }) => {
  const cls = "flex items-center gap-3 rounded-2xl border-2 border-[#A83232] bg-white px-5 py-4 text-lg font-semibold text-[#A83232] transition-colors hover:bg-[#FFF3EE]";
  return href
    ? <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className={cls} data-testid={testid}><Icon className="h-6 w-6" />{label}</a>
    : <button type="button" onClick={onClick} className={cls} data-testid={testid}><Icon className="h-6 w-6" />{label}</button>;
};

export default function Share() {
  const [params] = useSearchParams();
  const url = safeUrl(params.get("v"));
  const title = (params.get("t") || "").slice(0, 60);
  if (!url) {
    return <div className="mx-auto max-w-xl px-6 py-20 text-center text-xl" data-testid="share-invalid">This share link isn't valid. Please use the link from your email.</div>;
  }
  const msg = `Watch our family video${title ? ` "${title}"` : ""}: ${url}`;
  const enc = encodeURIComponent;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Couldn't copy. Press and hold the link to copy it."); }
  };
  const native = () => navigator.share({ title: title || "Our family video", text: msg, url }).catch(() => {});
  return (
    <div className="mx-auto max-w-xl px-6 py-14" data-testid="share-page">
      <p className="font-script text-3xl text-[#A83232]">Share the memory</p>
      <h1 className="mt-2 text-4xl font-bold text-[#4A2A18] sm:text-5xl" data-testid="share-title">{title || "Your family video"}</h1>
      <p className="mt-4 text-lg text-[#4A3B32]">Send this video to family and friends. Only people with the link can watch it.</p>
      <a href={url} target="_blank" rel="noreferrer" className="btn-primary mt-6 inline-flex items-center gap-2" data-testid="share-watch-link"><Play className="h-5 w-5" />Watch the video</a>
      <div className="mt-8 grid gap-3">
        {typeof navigator.share === "function" && <Option onClick={native} icon={Share2} label="Share…" testid="share-native-button" />}
        <Option href={`sms:?&body=${enc(msg)}`} icon={MessageSquare} label="Send by text message" testid="share-sms-link" />
        <Option href={`mailto:?subject=${enc("A family video for you")}&body=${enc(msg)}`} icon={Mail} label="Send by email" testid="share-email-link" />
        <Option href={`https://wa.me/?text=${enc(msg)}`} icon={MessageCircle} label="Send on WhatsApp" testid="share-whatsapp-link" />
        <Option href={`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`} icon={Facebook} label="Share on Facebook" testid="share-facebook-link" />
        <Option onClick={copy} icon={Copy} label="Copy link" testid="share-copy-button" />
      </div>
      <p className="mt-6 break-all text-sm text-[#8C7A6B]" data-testid="share-url">{url}</p>
    </div>
  );
}
